import { Box, styled } from '@mui/material'
import { FC, useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeKatex from 'rehype-katex'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'

import 'katex/dist/katex.min.css'

interface MarkdownMessageProps {
  content: string
}

// Renders assistant/chat replies as GitHub-flavored Markdown with LaTeX math
// (via KaTeX). remark-math only recognizes `$…$` / `$$…$$`, but LLMs also emit
// the LaTeX-native `\(…\)` (inline) and `\[…\]` (display) delimiters — normalize
// those to dollar delimiters first so all of them render.
const normalizeMath = (raw: string): string => {
  let out = raw
  // Display math: \[ … \]  →  $$ … $$  (allow multi-line content).
  out = out.replace(/\\\[([\s\S]*?)\\\]/g, (_m, body) => `\n$$\n${body.trim()}\n$$\n`)
  // Inline math: \( … \)  →  $ … $
  out = out.replace(/\\\(([\s\S]*?)\\\)/g, (_m, body) => `$${body.trim()}$`)
  return out
}

// A `$` immediately followed by a digit is currency, not a math delimiter.
// Left alone, remark-math pairs the `$` in "$2.41 million" with the next one in
// "$300k" and renders the prose between them as a TeX formula — spaces collapse
// and the text turns italic serif. Contract replies quote money constantly, so
// escape those. Known trade-off: inline math that *starts* with a digit
// ("$2x + 1$") also gets escaped — far rarer here than dollar amounts.
const CURRENCY_DOLLAR = /(?<!\\)\$(?=\d)/g

const escapeCurrency = (raw: string): string => {
  let inFence = false
  let inDisplayMath = false
  return raw
    .split('\n')
    .map((line) => {
      const t = line.trim()
      // Never touch fenced code — a `$100` there is literal source.
      if (t.startsWith('```')) {
        inFence = !inFence
        return line
      }
      if (inFence) return line
      // Skip $$…$$ display blocks; a one-line `$$x$$` opens and closes at once.
      if (t.startsWith('$$')) {
        if (!(t.length > 2 && t.endsWith('$$'))) inDisplayMath = !inDisplayMath
        return line
      }
      if (inDisplayMath) return line
      return line.replace(CURRENCY_DOLLAR, '\\$')
    })
    .join('\n')
}

const MarkdownMessage: FC<MarkdownMessageProps> = ({ content }) => {
  // Escape currency *after* \(…\) → $…$ normalization, so it sees the same
  // delimiters remark-math will.
  const normalized = useMemo(
    () => escapeCurrency(normalizeMath(content)),
    [content],
  )

  return (
    <MarkdownRoot>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          // Open links in a new tab safely.
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer" />
          ),
        }}
      >
        {normalized}
      </ReactMarkdown>
    </MarkdownRoot>
  )
}

const MarkdownRoot = styled(Box)({
  // Inherit the surrounding bubble's text color (theme-aware in dark mode);
  // the global MUI theme otherwise paints headings/cells a fixed grey.
  color: 'inherit',
  '& > *:first-of-type': { marginTop: 0 },
  '& > *:last-child': { marginBottom: 0 },
  '& p': { margin: '0 0 8px', color: 'inherit' },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    margin: '12px 0 6px',
    fontWeight: 600,
    lineHeight: 1.3,
    color: 'inherit',
  },
  '& li, & strong, & em, & span': { color: 'inherit' },
  '& h1': { fontSize: 18 },
  '& h2': { fontSize: 16 },
  '& h3': { fontSize: 15 },
  '& h4, & h5, & h6': { fontSize: 14 },
  '& ul, & ol': { margin: '0 0 8px', paddingLeft: 20 },
  '& li': { margin: '2px 0' },
  '& blockquote': {
    margin: '8px 0',
    paddingLeft: 12,
    borderLeft: '3px solid var(--chat-border, rgba(0, 0, 0, 0.12))',
    color: 'var(--chat-text-secondary, #4b5563)',
  },
  // Block code (<pre>)
  '& pre': {
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
    fontSize: '0.85em',
    backgroundColor: 'var(--chat-input-bg, rgba(0, 0, 0, 0.04))',
    border: '1px solid var(--chat-border, rgba(0, 0, 0, 0.08))',
    padding: '12px',
    borderRadius: '8px',
    overflowX: 'auto',
    margin: '12px 0',
  },
  '& pre code': {
    background: 'transparent',
    padding: 0,
    border: 'none',
    fontSize: 'inherit',
    color: 'inherit',
  },
  // Inline code
  '& code': {
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
    fontSize: '0.85em',
    backgroundColor: 'var(--chat-input-bg, rgba(0, 0, 0, 0.06))',
    color: 'var(--chat-code, #eb5757)',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  '& a': {
    color: 'var(--chat-accent, #2563eb)',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  '& strong': { fontWeight: 600 },
  '& table': {
    borderCollapse: 'collapse',
    margin: '12px 0',
    fontSize: '0.9em',
    lineHeight: 1.4,
    width: '100%',
    display: 'block',
    overflowX: 'auto',
  },
  '& th, & td': {
    border: '1px solid var(--chat-border, rgba(0, 0, 0, 0.12))',
    padding: '8px 12px',
    verticalAlign: 'top',
    color: 'inherit',
  },
  '& th': {
    background: 'var(--chat-hover, rgba(0, 0, 0, 0.03))',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    color: 'inherit',
  },
  // KaTeX display blocks: let wide equations scroll instead of overflowing the bubble.
  '& .katex-display': {
    margin: '10px 0',
    overflowX: 'auto',
    overflowY: 'hidden',
  },
})

export default MarkdownMessage
